import { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'
import { useCallback, useState } from 'react'

import { classNames } from '@/utils'

const DEFAULT_PAGINATION = 10

/** Matches TableApp empty state copy */
export const AG_GRID_NO_ROWS_MESSAGE = 'There is not data to show'

const OVERLAY_NO_ROWS_TEMPLATE = `<span class="text-sm text-gray-400">${AG_GRID_NO_ROWS_MESSAGE}</span>`

interface AgGridAppProps extends AgGridReactProps {
  rowData: any
  columnDefs: any
  isShowPagination?: boolean
  isShowFilter?: boolean
  /** Grow to fill parent flex height (use inside flex-col + min-h-0 containers) */
  fillHeight?: boolean
  minHeight?: number
  /** Extra classes on the theme wrapper (e.g. ops-ag-grid for operations layout) */
  className?: string
}

export const AgGridApp = ({
  rowData,
  columnDefs,
  isShowPagination = true,
  isShowFilter = true,
  fillHeight = false,
  minHeight = 200,
  paginationPageSize = DEFAULT_PAGINATION,
  onPaginationChanged,
  className,
  ...props
}: AgGridAppProps) => {
  const [paginationLength, setPaginationLength] = useState(paginationPageSize)

  const getTableHeight = useCallback(() => {
    const itemsLength = Math.min(paginationLength, rowData.length || 1)
    return 100 + itemsLength * 42
  }, [rowData, paginationLength])

  const handlePaginationChanged: AgGridReactProps['onPaginationChanged'] = (event) => {
    onPaginationChanged?.(event)
    if (isShowPagination) {
      setPaginationLength(event.api.paginationGetPageSize())
    }
  }

  const sharedProps: AgGridReactProps = {
    rowData,
    columnDefs,
    defaultColDef: {
      filter: 'agTextColumnFilter',
      floatingFilter: isShowFilter,
      flex: 1,
    },
    enableCellTextSelection: true,
    pagination: isShowPagination,
    paginationPageSize,
    paginationPageSizeSelector: [10, 20, 30, 40, 50, 100],
    overlayNoRowsTemplate: OVERLAY_NO_ROWS_TEMPLATE,
    rowClass: 'text-gray-600 text-sm h-auto',
    onPaginationChanged: handlePaginationChanged,
    ...props,
  }

  if (!fillHeight) {
    return (
      <div className={classNames('ag-theme-quartz w-full', className)} style={{ height: getTableHeight() }}>
        <AgGridReact className="xs:hidden md:block" {...sharedProps} />
        <AgGridReact
          className="xs:block md:hidden"
          {...sharedProps}
          defaultColDef={{
            filter: 'agTextColumnFilter',
            floatingFilter: isShowFilter,
          }}
        />
      </div>
    )
  }

  const gridStyle = { height: '100%', width: '100%' }

  return (
    <div
      className={classNames('ag-theme-quartz w-full flex flex-col flex-1 min-h-0 h-full', className)}
      style={{ minHeight }}
    >
      <div className="flex-1 min-h-0 h-full" style={gridStyle}>
        <AgGridReact
          className="xs:hidden md:block h-full w-full"
          style={gridStyle}
          {...sharedProps}
        />
        <AgGridReact
          className="xs:block md:hidden h-full w-full"
          style={gridStyle}
          {...sharedProps}
          defaultColDef={{
            filter: 'agTextColumnFilter',
            floatingFilter: isShowFilter,
          }}
        />
      </div>
    </div>
  )
}
